"use client"

import { createContext, useContext, useReducer, type Dispatch } from "react"

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface State {
  redirectTo: string
  authError: string | null
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type Action =
  | { type: "REDIRECT_TARGET_SET"; payload: { redirectTo: string } }
  | { type: "AUTH_ERROR_SET"; payload: { authError: string | null } }

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "REDIRECT_TARGET_SET":
      return { ...state, redirectTo: action.payload.redirectTo }
    case "AUTH_ERROR_SET":
      return { ...state, authError: action.payload.authError }
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ContextValue {
  state: State
  dispatch: Dispatch<Action>
}

const PageContext = createContext<ContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface PageProviderProps {
  children: React.ReactNode
  redirectTo: string
  authError: string | null
}

export function PageProvider({
  children,
  redirectTo,
  authError,
}: PageProviderProps): React.ReactElement {
  const initialState: State = { redirectTo, authError }
  const [state, dispatch] = useReducer(reducer, initialState)

  return (
    <PageContext.Provider value={{ state, dispatch }}>
      {children}
    </PageContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLoginContext(): ContextValue {
  const context = useContext(PageContext)
  if (!context) {
    throw new Error("useLoginContext must be used within PageProvider")
  }
  return context
}
